import React from 'react';
import {
  AgnesAlert,
  AgnesAlertContent,
  AgnesBadge,
  AgnesButton,
  AgnesInput,
  AgnesLabel,
  AgnesSelect,
  AgnesSelectContent,
  AgnesSelectItem,
  AgnesSelectTrigger,
  AgnesSelectValue,
  AgnesTextarea,
} from '@agnes/ui';
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Info,
  Layers,
  Shield,
  Sparkles,
} from 'lucide-react';
import { BaseComponentProps } from '@/components/BaseComponentProps';
import { SectionCard } from '@/components/ui/SectionCard';
import { GRAU_EXPOSICAO_RISCO_NOMES } from '@/constants/grau-exposicao-risco/GrauExposicaoRisco';
import { AnaliseGrauExposicaoRisco } from '@/types/analise/AnaliseGrauExposicaoRisco';
import { Rubrica } from '@/types/rubricas/Rubrica';
import {
  type AcaoCampo,
  ACOES_CAMPO,
  type CampoEdicao,
  CAMPOS_INCIDENCIA,
  type EdicaoAcaoEmMassa,
  incidenciasExibidas,
  OPCOES_TETO,
  ORIGEM_SELECAO_MULTIPLA,
  TIPOS_RUBRICA,
  type ValoresIncidencia,
} from '@/pages/folha-pagamento/acao-em-massa/acaoEmMassa';
import { PainelRubricasAfetadas } from '@/pages/folha-pagamento/acao-em-massa/PainelRubricasAfetadas';
import { SecaoRecolhivel } from '@/pages/folha-pagamento/acao-em-massa/SecaoRecolhivel';
import type { CatalogosAcaoEmMassa } from '@/pages/folha-pagamento/acao-em-massa/useCatalogosAcaoEmMassa';
import { BadgeGrauExposicaoRisco } from '@/components/analise/BadgeGrauExposicaoRisco';

export interface EtapaCamposAfetadosProps extends BaseComponentProps {
  origem: string;
  /** Rubricas mantidas na etapa 1 (antes de remover naturezas). */
  rubricas: Rubrica[];
  /** Rubricas que de fato receberão a ação. */
  efetivas: number;
  edicao: EdicaoAcaoEmMassa;
  onEdicaoChange: (update: (atual: EdicaoAcaoEmMassa) => EdicaoAcaoEmMassa) => void;
  catalogos: CatalogosAcaoEmMassa;
  /** Incidências exibidas enquanto não há exceção manual. */
  recomendadas: ValoresIncidencia;
  pendencias: string[];
  salvando: boolean;
  onVoltar: () => void;
  onSalvar: () => void;
}

// Espelha RubricaLoteGrauRiscoManual do BFF, do maior para o menor.
const GRAUS_MANUAIS: AnaliseGrauExposicaoRisco[] = [
  'CRITICO',
  'ELEVADO',
  'MODERADO',
  'BAIXO',
  'INEXPRESSIVO',
];
const SEM_RISCO_MANUAL = 'AUTOMATICO';

// Trunca o valor longo dentro do trigger em vez de empurrar o chevron.
const TRIGGER = 'w-full [&>span]:min-w-0 [&>span]:truncate [&>span]:text-left';
const CAMPO_LEITURA = 'border-transparent bg-agnes-primary-light text-agnes-text-primary';

interface LinhaEdicaoCampoProps extends BaseComponentProps {
  campo: string;
  valor: CampoEdicao;
  opcoes: { value: string; label: string }[];
  onChange: (valor: CampoEdicao) => void;
}

function LinhaEdicaoCampo({ campo, valor, opcoes, onChange, dataQa }: LinhaEdicaoCampoProps) {
  const id = React.useId();
  return (
    <div
      data-qa={dataQa}
      className="grid grid-cols-1 items-end gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto]"
    >
      <AgnesInput label="Campo" value={campo} readOnly className={CAMPO_LEITURA} />

      <div className="flex min-w-0 flex-col gap-1.5">
        <AgnesLabel htmlFor={`${id}-acao`} required>
          Ação
        </AgnesLabel>
        <AgnesSelect
          value={valor.acao}
          onValueChange={(acao) => onChange({ acao: acao as AcaoCampo, valor: valor.valor })}
        >
          <AgnesSelectTrigger id={`${id}-acao`} dataQa={`${dataQa}-acao`} className={TRIGGER}>
            <AgnesSelectValue />
          </AgnesSelectTrigger>
          <AgnesSelectContent>
            {ACOES_CAMPO.map((acao) => (
              <AgnesSelectItem
                key={acao.value}
                dataQa={`${dataQa}-acao-${acao.value}`}
                value={acao.value}
              >
                {acao.label}
              </AgnesSelectItem>
            ))}
          </AgnesSelectContent>
        </AgnesSelect>
      </div>

      {valor.acao === 'SUBSTITUIR' ? (
        <div className="flex min-w-0 flex-col gap-1.5">
          <AgnesLabel htmlFor={`${id}-valor`} required>
            {campo}
          </AgnesLabel>
          <AgnesSelect
            value={valor.valor}
            onValueChange={(novo) => onChange({ ...valor, valor: novo })}
          >
            <AgnesSelectTrigger id={`${id}-valor`} dataQa={`${dataQa}-valor`} className={TRIGGER}>
              <AgnesSelectValue placeholder={`Selecione ${campo.toLowerCase()}`} />
            </AgnesSelectTrigger>
            <AgnesSelectContent>
              {opcoes.map((opcao) => (
                <AgnesSelectItem
                  key={opcao.value}
                  dataQa={`${dataQa}-valor-${opcao.value}`}
                  value={opcao.value}
                >
                  {opcao.label}
                </AgnesSelectItem>
              ))}
            </AgnesSelectContent>
          </AgnesSelect>
        </div>
      ) : (
        <AgnesInput
          label="Resultado"
          dataQa={`${dataQa}-resultado`}
          value={valor.acao === 'LIMPAR' ? 'Será removido' : 'Não será alterado'}
          readOnly
          className={CAMPO_LEITURA}
        />
      )}

      <AgnesButton
        dataQa={`${dataQa}-limpar`}
        variant="ghost"
        className="font-semibold text-agnes-primary"
        aria-label={`Limpar ${campo}`}
        onClick={() => onChange({ acao: 'MANTER', valor: '' })}
      >
        Limpar
      </AgnesButton>
    </div>
  );
}

function SeloSecao({ destaque, children }: { destaque: boolean; children: React.ReactNode }) {
  return (
    <AgnesBadge size="sm" variant={destaque ? 'warning' : 'success'}>
      {children}
    </AgnesBadge>
  );
}

export function EtapaCamposAfetados({
  origem,
  rubricas,
  efetivas,
  edicao,
  onEdicaoChange,
  catalogos,
  recomendadas,
  pendencias,
  salvando,
  onVoltar,
  onSalvar,
  dataQa = 'acao-massa-campos',
}: EtapaCamposAfetadosProps) {
  const incidencias = incidenciasExibidas(edicao, recomendadas);
  const excecaoIncidencias = edicao.incidencias !== null;
  const opcoesNatureza = React.useMemo(
    () =>
      catalogos.naturezas.map((natureza) => ({
        value: natureza.codigo,
        label: `${natureza.codigo} - ${natureza.nome}`,
      })),
    [catalogos.naturezas],
  );

  const alterarIncidencia = (campo: keyof ValoresIncidencia, valor: string) =>
    onEdicaoChange((atual) => ({
      ...atual,
      incidencias: { ...atual.incidencias, [campo]: valor },
    }));

  return (
    <SectionCard data-qa={dataQa} className="overflow-hidden p-0">
      <div className="border-b border-agnes-border bg-agnes-bg-subtle px-5 py-4">
        <p className="text-sm text-agnes-text-muted">Descrição agrupada</p>
        <h2 className="text-lg font-semibold text-agnes-primary">{origem}</h2>
      </div>

      <div className="flex flex-col gap-4 p-5">
        <div>
          <p className="font-semibold text-agnes-primary">
            Edição em massa | {rubricas.length.toLocaleString('pt-BR')}{' '}
            {rubricas.length === 1 ? 'rubrica' : 'rubricas'}
          </p>
          <p className="mt-1 text-sm text-agnes-text-muted">
            Os campos vêm <b>carregados automaticamente</b>. A alteração é <b>opcional</b> — ajuste
            apenas o que precisar; a descrição em si permanece intacta.
          </p>
        </div>

        <AgnesAlert
          dataQa={`${dataQa}-descricao`}
          icon={<Info className="h-4 w-4 text-agnes-primary" />}
          className="bg-agnes-primary/5"
        >
          <AgnesAlertContent dataQa={`${dataQa}-descricao`}>
            {origem === ORIGEM_SELECAO_MULTIPLA ? (
              <>
                Em <b>seleção múltipla</b> (descrições distintas), a{' '}
                <b>descrição não pode ser alterada</b> — apenas tipo, natureza e incidências.
              </>
            ) : (
              <>
                A <b>descrição não é alterada</b> pela ação em massa — apenas tipo, natureza e
                incidências.
              </>
            )}
          </AgnesAlertContent>
        </AgnesAlert>

        {catalogos.erro && (
          <AgnesAlert dataQa={`${dataQa}-catalogos-erro`} preset="warning">
            <AgnesAlertContent dataQa={`${dataQa}-catalogos-erro`}>
              Não foi possível carregar todas as opções de natureza e incidência. Tente novamente
              mais tarde.
            </AgnesAlertContent>
          </AgnesAlert>
        )}

        <LinhaEdicaoCampo
          dataQa={`${dataQa}-tipo`}
          campo="Tipo"
          valor={edicao.tipo}
          opcoes={TIPOS_RUBRICA}
          onChange={(tipo) => onEdicaoChange((atual) => ({ ...atual, tipo }))}
        />
        <LinhaEdicaoCampo
          dataQa={`${dataQa}-natureza`}
          campo="Natureza"
          valor={edicao.natureza}
          opcoes={opcoesNatureza}
          onChange={(natureza) => onEdicaoChange((atual) => ({ ...atual, natureza }))}
        />

        <PainelRubricasAfetadas
          rubricas={rubricas}
          removidas={edicao.naturezasRemovidas}
          naturezas={catalogos.naturezas}
          onRemovidasChange={(naturezasRemovidas) =>
            onEdicaoChange((atual) => ({ ...atual, naturezasRemovidas }))
          }
        />

        <SecaoRecolhivel
          dataQa={`${dataQa}-incidencias`}
          titulo="Campos de incidência"
          icone={<Layers className="h-5 w-5" />}
          selo={
            <SeloSecao destaque={excecaoIncidencias}>
              {excecaoIncidencias ? 'Exceção identificada' : 'Auto-preenchido pela natureza'}
            </SeloSecao>
          }
        >
          <div className="flex flex-col gap-4 p-5">
            {excecaoIncidencias ? (
              <AgnesAlert
                dataQa={`${dataQa}-incidencias-excecao`}
                preset="warning"
                icon={<AlertTriangle className="h-4 w-4" />}
              >
                <AgnesAlertContent dataQa={`${dataQa}-incidencias-excecao`}>
                  <b>Exceção identificada:</b> as incidências foram <b>editadas manualmente</b> e
                  podem divergir da recomendação da tabela eSocial para a natureza. A alteração será
                  registrada como exceção.{' '}
                  <AgnesButton
                    dataQa={`${dataQa}-incidencias-restaurar`}
                    variant="text"
                    size="sm"
                    onClick={() => onEdicaoChange((atual) => ({ ...atual, incidencias: null }))}
                  >
                    Restaurar recomendado
                  </AgnesButton>
                </AgnesAlertContent>
              </AgnesAlert>
            ) : (
              <AgnesAlert
                dataQa={`${dataQa}-incidencias-auto`}
                icon={<Sparkles className="h-4 w-4 text-agnes-primary" />}
                className="bg-agnes-primary/5"
              >
                <AgnesAlertContent dataQa={`${dataQa}-incidencias-auto`}>
                  Os códigos de incidência são <b>preenchidos automaticamente</b> a partir da
                  natureza selecionada (tabela eSocial) e <b>podem ser editados</b> manualmente.
                </AgnesAlertContent>
              </AgnesAlert>
            )}

            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
              {CAMPOS_INCIDENCIA.map(({ key, label }) => (
                <SelectIncidencia
                  key={key}
                  dataQa={`${dataQa}-incidencia-${key}`}
                  rotulo={label}
                  valor={incidencias[key]}
                  opcoes={catalogos.incidencias[key].map((opcao) => ({
                    value: opcao.code,
                    label: opcao.name ? `${opcao.code} - ${opcao.name}` : opcao.code,
                  }))}
                  onChange={(valor) => alterarIncidencia(key, valor)}
                />
              ))}
              <SelectIncidencia
                dataQa={`${dataQa}-incidencia-teto`}
                rotulo="TETO REMUNERATÓRIO"
                valor={incidencias.teto}
                opcoes={OPCOES_TETO.map((opcao) => ({ value: opcao, label: opcao }))}
                onChange={(valor) => alterarIncidencia('teto', valor)}
              />
            </div>
          </div>
        </SecaoRecolhivel>

        <SecaoRecolhivel
          dataQa={`${dataQa}-risco`}
          titulo="Grau de risco (exceção manual)"
          icone={<Shield className="h-5 w-5" />}
          abertaInicialmente={false}
          selo={
            <SeloSecao destaque={!!edicao.grauRisco}>
              {edicao.grauRisco ? 'Sobrepõe automação' : 'Automático'}
            </SeloSecao>
          }
        >
          <div className="flex flex-col gap-4 p-5">
            <AgnesAlert dataQa={`${dataQa}-risco-aviso`} preset="warning">
              <AgnesAlertContent dataQa={`${dataQa}-risco-aviso`}>
                O grau de risco é <b>calculado automaticamente</b> pela Agnes. Definir um valor aqui
                é <b>opcional</b> e reservado a <b>casos de exceção</b> — a escolha manual{' '}
                <b>sobrepõe qualquer automação</b> para as rubricas selecionadas.
              </AgnesAlertContent>
            </AgnesAlert>
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex min-w-60 flex-col gap-1.5">
                <AgnesLabel htmlFor={`${dataQa}-risco-select`}>
                  Grau de risco manual (opcional)
                </AgnesLabel>
                <AgnesSelect
                  value={edicao.grauRisco ?? SEM_RISCO_MANUAL}
                  onValueChange={(valor) =>
                    onEdicaoChange((atual) => ({
                      ...atual,
                      grauRisco:
                        valor === SEM_RISCO_MANUAL ? null : (valor as AnaliseGrauExposicaoRisco),
                    }))
                  }
                >
                  <AgnesSelectTrigger
                    id={`${dataQa}-risco-select`}
                    dataQa={`${dataQa}-risco-select`}
                    className={TRIGGER}
                  >
                    <AgnesSelectValue />
                  </AgnesSelectTrigger>
                  <AgnesSelectContent>
                    <AgnesSelectItem
                      dataQa={`${dataQa}-risco-opcao-automatico`}
                      value={SEM_RISCO_MANUAL}
                    >
                      — Não aplicar (manter automático)
                    </AgnesSelectItem>
                    {GRAUS_MANUAIS.map((grau) => (
                      <AgnesSelectItem
                        key={grau}
                        dataQa={`${dataQa}-risco-opcao-${grau}`}
                        value={grau}
                      >
                        {GRAU_EXPOSICAO_RISCO_NOMES[grau]}
                      </AgnesSelectItem>
                    ))}
                  </AgnesSelectContent>
                </AgnesSelect>
              </div>
              {edicao.grauRisco && (
                <div data-qa={`${dataQa}-risco-aplicado`} className="flex items-center gap-2 pb-2">
                  <span className="text-sm text-agnes-text-muted">Será aplicado:</span>
                  <BadgeGrauExposicaoRisco grau={edicao.grauRisco} />
                  <AgnesButton
                    dataQa={`${dataQa}-risco-limpar`}
                    variant="text"
                    size="sm"
                    onClick={() => onEdicaoChange((atual) => ({ ...atual, grauRisco: null }))}
                  >
                    Limpar
                  </AgnesButton>
                </div>
              )}
            </div>
          </div>
        </SecaoRecolhivel>

        <AgnesAlert
          dataQa={`${dataQa}-ocorrencias`}
          icon={<Sparkles className="h-4 w-4 text-agnes-primary" />}
          className="bg-agnes-primary/5"
        >
          <AgnesAlertContent dataQa={`${dataQa}-ocorrencias`}>
            As ocorrências são <b>recalculadas</b> a partir das recomendações aplicadas. Conflitos
            serão sinalizados na etapa de revisão.
          </AgnesAlertContent>
        </AgnesAlert>

        <AgnesTextarea
          dataQa={`${dataQa}-comentario`}
          label="Comentário da análise"
          placeholder="Registre observações, justificativas ou orientações sobre esta ação em massa…"
          hint="O comentário acompanha a ação no histórico e na auditoria do lote."
          rows={3}
          maxLength={1000}
          value={edicao.comentario}
          onChange={(event) => {
            const comentario = event.target.value;
            onEdicaoChange((atual) => ({ ...atual, comentario }));
          }}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-agnes-border px-5 py-4">
        <AgnesButton
          dataQa={`${dataQa}-voltar`}
          variant="text"
          startIcon={<ChevronLeft className="h-4 w-4" />}
          disabled={salvando}
          onClick={onVoltar}
        >
          Voltar
        </AgnesButton>
        <div className="flex flex-wrap items-center gap-3">
          {(pendencias.length > 0 || efetivas === 0) && (
            <span
              data-qa={`${dataQa}-pendencias`}
              role="status"
              className="text-sm text-agnes-error"
            >
              {efetivas === 0 ? 'Selecione ao menos uma rubrica afetada.' : pendencias.join(' ')}
            </span>
          )}
          <AgnesButton
            dataQa={`${dataQa}-salvar`}
            variant="primary"
            startIcon={<ChevronRight className="h-4 w-4" />}
            loading={salvando}
            disabled={pendencias.length > 0 || efetivas === 0}
            onClick={onSalvar}
          >
            Salvar e avançar
          </AgnesButton>
        </div>
      </div>
    </SectionCard>
  );
}

interface SelectIncidenciaProps extends BaseComponentProps {
  rotulo: string;
  valor: string;
  opcoes: { value: string; label: string }[];
  onChange: (valor: string) => void;
}

function SelectIncidencia({ rotulo, valor, opcoes, onChange, dataQa }: SelectIncidenciaProps) {
  const id = React.useId();
  // O valor atual pode não estar no catálogo (ex.: código legado); mantém ele selecionável.
  const lista =
    valor && !opcoes.some((opcao) => opcao.value === valor)
      ? [{ value: valor, label: valor }, ...opcoes]
      : opcoes;
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <AgnesLabel htmlFor={id} className="text-xs font-semibold uppercase text-agnes-text-muted">
        {rotulo}
      </AgnesLabel>
      <AgnesSelect value={valor} onValueChange={onChange}>
        <AgnesSelectTrigger id={id} dataQa={dataQa} className={TRIGGER}>
          <AgnesSelectValue placeholder="Manter atual" />
        </AgnesSelectTrigger>
        <AgnesSelectContent>
          {lista.map((opcao) => (
            <AgnesSelectItem
              key={opcao.value}
              dataQa={`${dataQa}-${opcao.value}`}
              value={opcao.value}
            >
              {opcao.label}
            </AgnesSelectItem>
          ))}
        </AgnesSelectContent>
      </AgnesSelect>
    </div>
  );
}
